import { useState } from 'react';
import { Icon, Button } from '@/components/atoms';
import type { Discount } from '@/schemas/discounts';
import { DiscountType } from '@/schemas/discounts';

interface DiscountListProps {
  discounts: Discount[];
  onEdit: (discount: Discount) => void;
  onDelete: (id: string) => Promise<void>;
  onToggle: (id: string, isActive: boolean) => Promise<void>;
  onDuplicate: (discount: Discount) => void;
  isLoading?: boolean;
}

export const DiscountList: React.FC<DiscountListProps> = ({
  discounts,
  onEdit,
  onDelete,
  onToggle,
  onDuplicate,
  isLoading = false,
}) => {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const getDiscountTypeLabel = (type: DiscountType): string => {
    const labels: Record<DiscountType, string> = {
      [DiscountType.FLAT]: 'Flat Amount',
      [DiscountType.PERCENTAGE]: 'Percentage',
      [DiscountType.BUY_X_GET_Y]: 'Buy X Get Y',
      [DiscountType.SEASONAL]: 'Seasonal',
      [DiscountType.BULK]: 'Bulk Purchase',
    };
    return labels[type] || type;
  };

  const getDiscountTypeBadgeColor = (type: DiscountType): string => {
    const colors: Record<DiscountType, string> = {
      [DiscountType.FLAT]: 'bg-blue-100 text-blue-700',
      [DiscountType.PERCENTAGE]: 'bg-green-100 text-green-700',
      [DiscountType.BUY_X_GET_Y]: 'bg-purple-100 text-purple-700',
      [DiscountType.SEASONAL]: 'bg-orange-100 text-orange-700',
      [DiscountType.BULK]: 'bg-indigo-100 text-indigo-700',
    };
    return colors[type] || 'bg-neutral-100 text-neutral-700';
  };

  const getStatusBadge = (discount: Discount) => {
    const { status, is_active } = discount;

    if (status === 'expired') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-error-100 text-error-700">
          Expired
        </span>
      );
    }

    if (status === 'usage_limit_reached') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-warning-100 text-warning-700">
          Limit Reached
        </span>
      );
    }

    if (status === 'scheduled') {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-info-100 text-info-700">
          Scheduled
        </span>
      );
    }

    if (!is_active) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-neutral-100 text-neutral-700">
          Inactive
        </span>
      );
    }

    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-success-100 text-success-700">
        Active
      </span>
    );
  };

  const getDiscountValueDisplay = (discount: Discount): string => {
    if (discount.discount_type === DiscountType.FLAT) {
      return `₹${discount.value}`;
    } else if (discount.discount_type === DiscountType.PERCENTAGE) {
      return `${discount.value}%`;
    } else if (discount.discount_type === DiscountType.BUY_X_GET_Y) {
      return `Buy ${discount.buy_quantity} Get ${discount.get_quantity}`;
    } else if (discount.discount_type === DiscountType.BULK) {
      return `${discount.value}% (Min ${discount.buy_quantity || 0} items)`;
    }
    return `${discount.value}%`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const handleToggle = async (id: string, currentActive: boolean) => {
    setTogglingId(id);
    try {
      await onToggle(id, !currentActive);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm('Are you sure you want to delete this discount? This action cannot be undone.')
    ) {
      setDeletingId(id);
      try {
        await onDelete(id);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const getUsageProgress = (discount: Discount): number => {
    if (!discount.usage_limit) return 0;
    return (discount.current_usage / discount.usage_limit) * 100;
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-neutral-600">Loading discounts...</span>
        </div>
      </div>
    );
  }

  if (discounts.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
        <div className="text-center">
          <Icon name="Percent" size={48} className="mx-auto text-neutral-300 mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 mb-2">No Discounts Found</h3>
          <p className="text-neutral-600">
            Create your first discount to start offering promotions to customers.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Code / Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Value
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Validity
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Usage
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-neutral-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200">
            {discounts.map((discount) => (
              <tr key={discount.id} className="hover:bg-neutral-50 transition-colors">
                <td className="px-4 py-4">
                  <div>
                    <div className="font-medium text-neutral-900">{discount.code}</div>
                    <div className="text-sm text-neutral-600">{discount.name}</div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${getDiscountTypeBadgeColor(discount.discount_type)}`}
                  >
                    {getDiscountTypeLabel(discount.discount_type)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="font-medium text-neutral-900">
                    {getDiscountValueDisplay(discount)}
                  </div>
                  {discount.max_discount_amount &&
                    discount.discount_type === DiscountType.PERCENTAGE && (
                      <div className="text-xs text-neutral-600">
                        Max: ₹{discount.max_discount_amount}
                      </div>
                    )}
                </td>
                <td className="px-4 py-4">{getStatusBadge(discount)}</td>
                <td className="px-4 py-4">
                  <div className="text-sm">
                    <div className="text-neutral-900">{formatDate(discount.valid_from)}</div>
                    <div className="text-neutral-600">to {formatDate(discount.valid_until)}</div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="w-32">
                    {discount.usage_limit ? (
                      <>
                        <div className="flex items-center justify-between text-xs text-neutral-600 mb-1">
                          <span>
                            {discount.current_usage} / {discount.usage_limit}
                          </span>
                          <span>{Math.round(getUsageProgress(discount))}%</span>
                        </div>
                        <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${
                              getUsageProgress(discount) >= 90
                                ? 'bg-error-500'
                                : getUsageProgress(discount) >= 70
                                  ? 'bg-warning-500'
                                  : 'bg-success-500'
                            }`}
                            style={{ width: `${Math.min(getUsageProgress(discount), 100)}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <span className="text-xs text-neutral-600">Unlimited</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(discount.id, discount.is_active)}
                      disabled={togglingId === discount.id}
                      title={discount.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <Icon
                        name={discount.is_active ? 'ToggleRight' : 'ToggleLeft'}
                        size={18}
                        className={discount.is_active ? 'text-success-600' : 'text-neutral-400'}
                      />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onEdit(discount)} title="Edit">
                      <Icon name="Pencil" size={18} className="text-neutral-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDuplicate(discount)}
                      title="Duplicate"
                    >
                      <Icon name="Copy" size={18} className="text-neutral-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(discount.id)}
                      disabled={deletingId === discount.id}
                      title="Delete"
                    >
                      <Icon name="Trash2" size={18} className="text-error-600" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
